import React, { type FC, type ReactNode, useContext } from "react";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {
  AlertBannerProvider,
  KivuEnvConfigProvider,
  KivunovaI18nProvider,
  PlatformConfigProvider
} from "@kivunova/kivufrontendcommon";
import AuthProvider from "./providers/AuthProvider";
import HostPlatformConfigSync from "./providers/HostPlatformConfigSync";
import HostBusinessTypeConfigSync from "./providers/HostBusinessTypeConfigSync";
import CompanyBasicInfoSync from "./providers/CompanyBasicInfoSync";
import MembershipProvider from "./providers/MembershipProvider";
import MembershipContext from "./context/membershipContext";
import { Provider } from "react-redux";
import { store } from "./store/store";
import { appTheme } from "./theme/theme";
import ClarityTracker from "./analytics/ClarityTracker";
import ClarityClickListener from "./analytics/ClarityClickListener";

const queryClient = new QueryClient();

const AppProviders: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <React.StrictMode>
      <Provider store={store}>
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider theme={appTheme}>
              <CssBaseline />
              <KivunovaI18nProvider>
                <KivuEnvConfigProvider>
                  <AlertBannerProvider>
                    <PlatformConfigProvider>
                      <HostPlatformConfigSync />
                      <ClarityTracker />
                      <ClarityClickListener />
                      <MembershipProvider>
                        <AuthConnector>{children}</AuthConnector>
                      </MembershipProvider>
                    </PlatformConfigProvider>
                  </AlertBannerProvider>
                </KivuEnvConfigProvider>
              </KivunovaI18nProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </BrowserRouter>
      </Provider>
    </React.StrictMode>
  );
};

// Connector to pass membership updates from Auth to Membership
const AuthConnector: FC<{ children: ReactNode }> = ({ children }) => {
  const { updateMembershipStatus } = useContext(MembershipContext);

  return (
    <AuthProvider onMembershipUpdate={updateMembershipStatus}>
      <HostBusinessTypeConfigSync />
      <CompanyBasicInfoSync />
      {children}
    </AuthProvider>
  );
};

export default AppProviders;
